import { trans } from "@mongez/localization";
import { requiredRule, useFormControl } from "@mongez/react-form";
import { action } from "framework/config/action";
import { URLS } from "framework/config/urls";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import type { Attachment } from "~/design-system/types";
import { cn } from "~/shared/lib";
import { Button } from "../button";
import { Remove, Upload } from "../icons";
import type { BaseInputProps } from "./BaseInput";
import { ErrorInput } from "./ErrorInput";
import { LabelInput } from "./LabelInput";

type FileInputProps = BaseInputProps & {
  accept?: string;
  maxFiles?: number;
};

export function FileInput({
  classes,
  label,
  placeholder,
  accept = "image/*",
  maxFiles,
  ...props
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { id, name, error, changeValue } = useFormControl({
    ...props,
    rules: [requiredRule],
  });
  const [isUploading, setIsUploading] = useState(false);

  const [files, setFiles] = useState<Attachment[]>([]);

  const uploadFiles = action(URLS.uploads);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = [...(e.target.files || [])];

    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error(trans("noFileSelected"));
      return;
    }

    if (maxFiles && files.length + selectedFiles.length > maxFiles) {
      toast.error(trans("maxFilesReached", { max: maxFiles }));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("uploads[]", file);
      });
      const response = await uploadFiles(formData);

      const newFiles = [...files, ...response.data.uploads];

      setFiles(newFiles);
      changeValue(newFiles.map((file) => file.hash));
      props.onChange?.(newFiles.map((file) => file.hash));
    } catch (error) {
      toast.error(trans("uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = (file: Attachment) => {
    const filteredFiles = files.filter((f) => f.hash !== file.hash);
    setFiles(filteredFiles);
    changeValue(filteredFiles.map((file) => file.hash));
  };

  return (
    <div className={cn("flex flex-col gap-2", classes?.wrapper)}>
      <LabelInput
        htmlFor={id}
        className={cn("text-sm font-medium", classes?.label)}
        required={props.required}
      >
        {label}
      </LabelInput>
      <input
        id={id}
        ref={inputRef}
        type="file"
        name={name}
        hidden
        multiple={maxFiles ? maxFiles > 1 : props.multiple}
        accept={accept}
        onChange={handleUpload}
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={cn(
          "w-full justify-between gap-4 rounded-full border px-6 py-4 text-xs sm:text-sm lg:text-base",
          classes?.input,
        )}
      >
        <div className="flex grow flex-wrap gap-4">
          {isUploading
            ? trans("uploading")
            : files.length > 0
              ? files.map((file) => (
                  <div key={file.url} className="flex items-center gap-2">
                    <img
                      src={file.url}
                      alt={file.name}
                      className="h-6 w-6 rounded-sm border object-cover"
                    />

                    <Remove
                      className="h-4 w-4 text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(file);
                      }}
                    />
                  </div>
                ))
              : placeholder || trans("uploadFile")}
        </div>
        <Upload />
      </Button>
      <ErrorInput className={classes?.error}>{error}</ErrorInput>
    </div>
  );
}
